/** A tradingsymbol or an index name, rendered as a NEW-TAB link to its
 * TradingView chart (operator, 2026-08-11: reading a contract anywhere
 * meant retyping the ticker; widened the same night to "wherever we show
 * symbol", and on 2026-08-12 to the index each strategy page names).
 *
 * Both fall back to plain text when the ticker cannot be built with
 * certainty — an unmapped underlying, or a monthly contract whose expiry
 * day the symbol does not carry. A link to the WRONG chart is worse than
 * none, so the tooltip says why rather than the surface silently going dead.
 *
 * `className` is for SIZE, WEIGHT and FONT only. The component owns the
 * colour, because the colour is what distinguishes a live link from dead
 * text — a caller-supplied `text-ink` would fight `text-accent` on the same
 * CSS property and win or lose by stylesheet order, not by intent.
 */

import type { ReactElement } from "react";
import { tradingViewIndexUrl, tradingViewUrl } from "@/lib/tradingView";

/** ONE shared TradingView tab for the whole app (operator, 2026-08-12:
 * a morning of clicking contracts buried the bot under a dozen chart
 * tabs). Every link targets this same named window, so the first click
 * opens a tab and each one after it re-points that tab and brings it
 * forward — from any page, in any window.
 *
 * This is why there is no `rel="noopener"`: the flag makes the browser
 * create a FRESH top-level context and skip the name lookup entirely, so
 * it and tab reuse cannot both be had. The cost is that TradingView gets
 * a `window.opener` handle back to this app. Acceptable here because the
 * destination is one hardcoded, well-known HTTPS host that is never built
 * from user input — the reverse-tabnabbing case needs TradingView itself
 * to be hostile. `referrerPolicy` still withholds the Referer, so strategy
 * URLs and trade ids do not leak; unlike `rel="noreferrer"` it carries no
 * implicit noopener, so it does not break the reuse. */
const TV_WINDOW = "nss-tradingview";

/** The contract, in the mono face every symbol on the site uses. Callers
 * that KNOW the expiry (positional legs carry one) pass it in: it is
 * authoritative, and it is what makes monthly contracts linkable at all. */
export function ContractLink({
  symbol,
  expiry,
  className = "text-xs",
}: Readonly<{
  symbol: string | null | undefined;
  expiry?: string | null;
  className?: string;
}>): ReactElement {
  return (
    <TvLink
      label={symbol}
      url={tradingViewUrl(symbol, expiry)}
      className={`font-mono ${className}`}
      what="contract"
    />
  );
}

/** The index (`NIFTY`, `SENSEX`, …). Deliberately NOT mono: it sits in
 * prose and in the strategy page's headline instrument tile, never in a
 * column of symbols. */
export function IndexLink({
  name,
  className,
}: Readonly<{ name: string | null | undefined; className?: string }>): ReactElement {
  return (
    <TvLink label={name} url={tradingViewIndexUrl(name)} className={className ?? ""} what="index" />
  );
}

function TvLink({
  label,
  url,
  className,
  what,
}: Readonly<{
  label: string | null | undefined;
  url: string | null;
  className: string;
  what: string;
}>): ReactElement {
  if (!label) return <span className={className}>—</span>;
  if (url === null)
    return (
      <span className={className} title={`No TradingView chart for this ${what}`}>
        {label}
      </span>
    );
  return (
    <a
      href={url}
      target={TV_WINDOW}
      referrerPolicy="no-referrer"
      className={`inline-flex items-center gap-1 text-accent hover:underline ${className}`}
      title={`Open ${label} on TradingView`}
    >
      {label}
      <i className="ti ti-external-link text-[10px]" />
    </a>
  );
}
